import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { useAuth } from '@/lib/auth-guards';
import { useTRPC, type ApiError, type RouterOutputs } from '@/lib/trpc';

type ServedProviderCapabilities = RouterOutputs['getProviderCapabilities'];

/**
 * Fetches the served Provider Capability set (ADR 0275). Mounted once, by
 * `ProviderCapabilitiesGate`, above the router.
 *
 * The cache is scoped to the authenticated subject rather than being
 * installation-wide. Today every Provider holds the same set, so a shared key
 * would look sufficient — but the served contract is per-subject, and once
 * supervising providers hold different sets a shared key would serve one
 * Provider's capabilities to another.
 *
 * Cache contract: owned by this hook (the gate is its only fetching mounter);
 * key is the generated tRPC key with the subject added to the args object;
 * `staleTime: Infinity` because the reply is a compiled constant for the life of
 * a session; invalidated by unmount at sign-out, since the subject is part of
 * the key a different subject cannot read a previous one's entry; stampede
 * protection is TanStack's in-flight deduplication on the shared key.
 */
export function useProviderCapabilitiesQuery(
  enabled: boolean,
): UseQueryResult<ServedProviderCapabilities, ApiError> {
  const trpc = useTRPC();
  const { user } = useAuth();
  const options = trpc.getProviderCapabilities.queryOptions({});

  // The subject scopes the cache only, and must go in the args object at index
  // 1 — NOT into the path at index 0. tRPC's generated queryFn re-derives the
  // wire request from whatever key TanStack hands it at fetch time
  // (`getClientArgs` does `queryKey[0].join('.')`), so extending the path
  // renames the procedure and every call 404s. A third element is worse:
  // `isPrefixedQueryKey` is `length >= 3`, so the args object would be read as
  // the path and `.join` would throw. Extra sibling fields in the args object
  // are ignored by the transport, which reads only `args.input`, and
  // `trpc.getProviderCapabilities.queryFilter()` still partial-matches.
  const [path, keyOptions] = options.queryKey;
  // Cast through unknown: the subject is a cache-only sibling of `input`, which
  // tRPC's key type does not declare. The transport reads only `args.input`, so
  // the extra field never reaches the wire.
  const queryKey = [
    path,
    { ...keyOptions, subject: user?.id ?? '' },
  ] as unknown as typeof options.queryKey;

  return useQuery({
    ...options,
    queryKey,
    staleTime: Number.POSITIVE_INFINITY,
    enabled,
  });
}

/**
 * Reads the resolved set for the current subject. This is a cache read, not a
 * second request: the gate above the router has already resolved this key and
 * its observer keeps the entry from being collected. An unresolved key yields
 * no capabilities, so every consumer fails closed.
 */
export function useProviderCapabilities(): readonly string[] {
  return useProviderCapabilitiesQuery(false).data?.capabilities ?? [];
}
